import { formatRelativeTime } from "@/features/workspace/hooks/formatRelativeTime";
import type { ConnectSharedSpace, ConnectSpace, ConnectSpaceMode } from "@ando/core";
import { Badge, cn } from "@ando/ui";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { Link } from "@tanstack/react-router";
import { Bot, Building2, Clock, Database, FileText, Plug, Users2 } from "lucide-react";
import type * as React from "react";
import { type ConnectSpaceSummary, summaryOf } from "../hooks/useConnectSpaces.js";
import { roleLabel } from "./connectShared.js";

/**
 * SpaceCard — hub card for one Ando Connect Space (June 2026).
 *
 * Glassmorphism card per the HUB spec: translucent surface + hairline +
 * blur, a mode badge (collaborazione/chatbot) carrying an ICON (never colour
 * alone), grant chips by type, stacked member avatars with +N overflow, and
 * a relative last-activity stamp. The whole card is the click target (a
 * router ``Link`` to the detail shell) with a visible focus ring.
 *
 * DRY: relative time reuses the shared ``formatRelativeTime`` workspace
 * helper; counts come from the ``summaryOf`` reader on the list hook; badges
 * and tokens come from ``@ando/ui`` — no new visual language.
 */

const MODE_META: Record<
  ConnectSpaceMode,
  { readonly icon: React.ComponentType<{ size?: number; strokeWidth?: number }> }
> = {
  collaboration: { icon: Users2 },
  chatbot: { icon: Bot },
};

const GRANT_ICON: Record<string, React.ComponentType<{ size?: number; strokeWidth?: number }>> = {
  knowledge: Database,
  connector: Plug,
  document: FileText,
};

export interface SpaceCardProps {
  readonly space: ConnectSpace;
  /** Entrance-stagger delay in ms (caller sequences the grid). */
  readonly stagger?: number;
  readonly className?: string;
}

export interface SharedSpaceCardProps {
  /** A "shared with me" row (narrow member-side projection). */
  readonly space: ConnectSharedSpace;
  readonly stagger?: number;
  readonly className?: string;
}

function grantChips(summary: ConnectSpaceSummary): readonly { type: string; count: number }[] {
  const order = ["document", "knowledge", "connector"];
  return order
    .map((t) => ({ type: t, count: summary.grant_counts[t] ?? 0 }))
    .filter((c) => c.count > 0);
}

export function SpaceCard({ space, stagger = 0, className }: SpaceCardProps) {
  const { i18n } = useLingui();
  const summary = summaryOf(space);
  const mode = space.mode === "chatbot" ? "chatbot" : "collaboration";
  const ModeIcon = MODE_META[mode].icon;
  const isArchived = space.status === "archived";

  const memberCount = summary.member_count;
  const chips = grantChips(summary);
  const last = formatRelativeTime(space.updated_at);

  const modeLabel = mode === "chatbot" ? i18n._(msg`Chatbot`) : i18n._(msg`Collaborazione`);

  const grantLabel = (type: string, count: number): string => {
    switch (type) {
      case "document":
        return count === 1 ? i18n._(msg`1 doc`) : i18n._(msg`${count} doc`);
      case "knowledge":
        return count === 1 ? i18n._(msg`1 base`) : i18n._(msg`${count} basi`);
      case "connector":
        return count === 1 ? i18n._(msg`1 fonte`) : i18n._(msg`${count} fonti`);
      default:
        return String(count);
    }
  };

  return (
    <Link
      to="/workspace/connect/$spaceId"
      params={{ spaceId: space.id }}
      // motion: enter on transform/opacity only, respects reduced-motion via
      // the shared keyframe utility; press state does NOT shift layout.
      style={stagger ? { animationDelay: `${stagger}ms` } : undefined}
      className={cn(
        "group motion-safe:animate-item-in",
        "flex flex-col gap-3 rounded-[16px] border border-hairline/80 bg-white/55 p-4 backdrop-blur-xl",
        "shadow-[0_1px_2px_rgba(0,0,0,0.04),inset_0_1px_0_rgba(255,255,255,0.6)]",
        "transition-[box-shadow,transform] duration-200 ease-out",
        "hover:-translate-y-0.5 hover:border-hairline hover:bg-white/70 hover:shadow-[0_8px_24px_-12px_rgba(0,0,0,0.18)]",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/60 focus-visible:ring-offset-2 focus-visible:ring-offset-cream",
        "cursor-pointer",
        isArchived && "opacity-70",
        className,
      )}
    >
      {/* Top row — name + mode badge (icon+text). */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="truncate font-semibold text-[14px] text-ink-1">{space.name}</h3>
          {space.description ? (
            <p className="mt-0.5 line-clamp-2 text-[12px] text-ink-2">{space.description}</p>
          ) : null}
        </div>
        <Badge
          variant={mode === "chatbot" ? "accent" : "secondary"}
          className="shrink-0 gap-1 py-0.5 text-[10.5px]"
        >
          <ModeIcon size={11} strokeWidth={2.2} aria-hidden />
          {modeLabel}
        </Badge>
      </div>

      {/* Grant chips by type — what's shared, at a glance. */}
      {chips.length > 0 ? (
        <ul className="flex flex-wrap gap-1.5">
          {chips.map((c) => {
            const Icon = GRANT_ICON[c.type] ?? FileText;
            return (
              <li key={c.type}>
                <span className="inline-flex items-center gap-1 rounded-md border border-hairline bg-cream-deep/60 px-1.5 py-0.5 text-[10.5px] text-ink-2">
                  <Icon size={11} strokeWidth={2} aria-hidden />
                  {grantLabel(c.type, c.count)}
                </span>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="text-[11.5px] text-ink-2">{i18n._(msg`Nessuna fonte condivisa ancora`)}</p>
      )}

      {/* Footer — stacked avatars (+N) on the left, last activity on right. */}
      <div className="mt-auto flex items-center justify-between gap-2 pt-1">
        <div className="flex items-center gap-2">
          {memberCount > 0 ? (
            // The list projection exposes only a member COUNT (no identities),
            // so we render a NEUTRAL group glyph + count — never fabricated
            // initials that would read as real people.
            <span
              className="inline-flex items-center gap-1.5 text-[11px] text-ink-2"
              aria-label={i18n._(msg`${memberCount} membri`)}
            >
              <span
                aria-hidden
                className="flex h-6 w-6 items-center justify-center rounded-full border border-white/80 bg-cream-deep text-ink-2"
              >
                <Users2 size={12} strokeWidth={2} />
              </span>
              <span aria-hidden className="tabular-nums">
                {i18n._(msg`${memberCount} membri`)}
              </span>
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-[11px] text-ink-2">
              <Users2 size={12} strokeWidth={2} aria-hidden />
              {i18n._(msg`Nessun membro`)}
            </span>
          )}
        </div>

        {last ? (
          <span className="inline-flex items-center gap-1 text-[11px] text-ink-2 tabular-nums">
            <Clock size={11} strokeWidth={2} aria-hidden />
            {last}
          </span>
        ) : null}
      </div>
    </Link>
  );
}
SpaceCard.displayName = "SpaceCard";

/** Shared glass-card shell className — identical to the owned ``SpaceCard``. */
const CARD_SHELL = cn(
  "group motion-safe:animate-item-in",
  "flex flex-col gap-3 rounded-[16px] border border-hairline/80 bg-white/55 p-4 backdrop-blur-xl",
  "shadow-[0_1px_2px_rgba(0,0,0,0.04),inset_0_1px_0_rgba(255,255,255,0.6)]",
  "transition-[box-shadow,transform] duration-200 ease-out",
  "hover:-translate-y-0.5 hover:border-hairline hover:bg-white/70 hover:shadow-[0_8px_24px_-12px_rgba(0,0,0,0.18)]",
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/60 focus-visible:ring-offset-2 focus-visible:ring-offset-cream",
  "cursor-pointer",
);

/**
 * SharedSpaceCard — one "Condivisi con me" row.
 *
 * Same glass shell + mode badge + last-activity stamp as the owned
 * ``SpaceCard``, but it renders the MEMBER projection: the owning org as the
 * counterpart (``owner_org_name``) and the caller's own role — NOT member
 * avatars / grant chips, which the narrow ``/spaces/shared`` projection does
 * not carry. Clicking navigates to the SAME detail route (``GET
 * /connect/spaces/{id}`` already authorizes active members).
 */
export function SharedSpaceCard({ space, stagger = 0, className }: SharedSpaceCardProps) {
  const { i18n } = useLingui();
  const mode = space.mode === "chatbot" ? "chatbot" : "collaboration";
  const ModeIcon = MODE_META[mode].icon;
  const isArchived = space.status === "archived";
  const last = formatRelativeTime(space.last_activity_at);
  const modeLabel = mode === "chatbot" ? i18n._(msg`Chatbot`) : i18n._(msg`Collaborazione`);
  const memberRoleLabel =
    space.member_role === "editor" ? roleLabel("editor", i18n) : roleLabel("viewer", i18n);

  return (
    <Link
      to="/workspace/connect/$spaceId"
      params={{ spaceId: space.id }}
      style={stagger ? { animationDelay: `${stagger}ms` } : undefined}
      className={cn(CARD_SHELL, isArchived && "opacity-70", className)}
    >
      {/* Top row — name + mode badge (icon+text). */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="truncate font-semibold text-[14px] text-ink-1">{space.name}</h3>
          {space.description ? (
            <p className="mt-0.5 line-clamp-2 text-[12px] text-ink-2">{space.description}</p>
          ) : null}
        </div>
        <Badge
          variant={mode === "chatbot" ? "accent" : "secondary"}
          className="shrink-0 gap-1 py-0.5 text-[10.5px]"
        >
          <ModeIcon size={11} strokeWidth={2.2} aria-hidden />
          {modeLabel}
        </Badge>
      </div>

      {/* Counterpart org — who shared this Space with me. Icon + text. */}
      {space.owner_org_name ? (
        <span className="inline-flex w-fit items-center gap-1.5 rounded-md border border-hairline bg-cream-deep/60 px-1.5 py-0.5 text-[11px] text-ink-2">
          <Building2 size={12} strokeWidth={2} aria-hidden />
          <span className="truncate">{space.owner_org_name}</span>
        </span>
      ) : null}

      {/* Footer — my role on the left, last activity on the right. */}
      <div className="mt-auto flex items-center justify-between gap-2 pt-1">
        <Badge variant="secondary" className="gap-1 py-0.5 text-[10.5px]">
          <Users2 size={11} strokeWidth={2.2} aria-hidden />
          {memberRoleLabel}
        </Badge>
        {last ? (
          <span className="inline-flex items-center gap-1 text-[11px] text-ink-2 tabular-nums">
            <Clock size={11} strokeWidth={2} aria-hidden />
            {last}
          </span>
        ) : null}
      </div>
    </Link>
  );
}
SharedSpaceCard.displayName = "SharedSpaceCard";
